import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, EmptyValue, Form, Modal, Spinner, StatusBadge } from '@/components/ui';
import { notifyApprovalsChanged } from '../../../components/awaitingApprovalsCount';
import {
  APPROVAL_KIND_LABEL,
  TRANSFER_STATUS_LABEL,
  formatDay,
  formatWhen,
  vehicleTransferService,
  type ApprovalKind,
  type TransferApproval,
  type TransferDetail,
  type TransferFile,
} from '../../../services/vehicleTransferService';
import RequestTransferModal from '../components/RequestTransferModal';
import TransferPersonPicker, { type PickedPerson } from '../components/TransferPersonPicker';
import { transferProgress } from './TransfersPage';
import { transferEventText } from '../utils/transferHistory';
import { usePermissions } from '../../../hooks/usePermissions';
import { EQUIPMENT_PERMISSIONS } from '../utils/equipmentPermissions';



const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="tw-text-xs tw-tracking-wide tw-text-content-muted">{label}</dt>
    <dd className="tw-mb-0 tw-text-sm tw-text-content">{children || <EmptyValue />}</dd>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="tw-mb-4 tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-4 tw-shadow-card">
    <h2 className="tw-mb-3 tw-text-xs tw-font-semibold tw-tracking-wide tw-text-content-secondary">{title}</h2>
    {children}
  </section>
);

/** When and how an approver decided. A pending approver's pill already says Waiting (V3-069). */
function decisionText(a: TransferApproval): string {
  if (a.decision === 'PENDING') return '';
  if (a.autoApproved) return `Signed by submitting · ${formatWhen(a.decidedAt)}`;
  return `${a.decision === 'APPROVED' ? 'Approved' : 'Rejected'} · ${formatWhen(a.decidedAt)}`;
}

type Dialog = { kind: 'approve' } | { kind: 'reject' } | { kind: 'complete' } | { kind: 'cancel' } | { kind: 'reassign'; slot: ApprovalKind } | { kind: 'return' } | null;

/** A transfer's pictures or documents, each opening the file (V3-057). */
const TransferFileList: React.FC<{ transferId: number; files: TransferFile[]; empty: string }> = ({ transferId, files, empty }) =>
  files.length === 0 ? (
    <p className="tw-mb-0 tw-text-sm tw-text-content-muted">{empty}</p>
  ) : (
    <ul className="tw-mb-0 tw-divide-y tw-divide-line tw-rounded-lg tw-border tw-border-line tw-pl-0">
      {files.map((f) => {
        const url = vehicleTransferService.fileUrl(transferId, f.id);
        const image = (f.mimeType ?? '').startsWith('image/');
        return (
          <li key={f.id} className="tw-flex tw-list-none tw-items-center tw-gap-3 tw-px-3 tw-py-2">
            {image ? (
              <img src={url} alt="" className="tw-h-10 tw-w-10 tw-shrink-0 tw-rounded tw-object-cover" />
            ) : (
              <i
                className={`bi ${/pdf/i.test(f.mimeType ?? f.name) ? 'bi-file-earmark-pdf' : /word|docx?$/i.test(f.mimeType ?? f.name) ? 'bi-file-earmark-word' : 'bi-file-earmark'} tw-w-10 tw-shrink-0 tw-text-center tw-text-2xl tw-text-content-muted`}
                aria-hidden="true"
              />
            )}
            <a href={url} target="_blank" rel="noopener noreferrer" className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm" title={f.name}>
              {f.photoSide ? `${f.photoSide.charAt(0)}${f.photoSide.slice(1).toLowerCase()} photo` : f.name}
            </a>
          </li>
        );
      })}
    </ul>
  );

const TransferDetailPage: React.FC = () => {
  const { transferId } = useParams<{ transferId: string }>();
  const navigate = useNavigate();
  const id = Number(transferId);
  const [t, setT] = useState<TransferDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [text, setText] = useState('');
  const [sageDate, setSageDate] = useState('');
  const [person, setPerson] = useState<PickedPerson | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const { hasPermission } = usePermissions();

  const load = useCallback(async () => {
    try {
      setT(await vehicleTransferService.get(id));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the transfer');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = (d: Dialog) => {
    setDialog(d);
    setText('');
    setPerson(null);
    setActionError(null);
    setSageDate(new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Vancouver' }).format(new Date()));
  };

  const act = async (fn: () => Promise<TransferDetail>) => {
    setBusy(true);
    setActionError(null);
    try {
      setT(await fn());
      setDialog(null);
      notifyApprovalsChanged();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="tw-w-full tw-p-4">
        <Alert variant="danger">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate('/equipment/transfers')}>
          Back to Transfers
        </Button>
      </div>
    );
  }
  if (!t) {
    return (
      <div className="tw-w-full tw-p-4 tw-py-10 tw-text-center">
        <Spinner role="status" />
      </div>
    );
  }

  // The unit page needs vehicle:view as well as row scope; Finance has the scope but not the key.
  const canOpenUnit = t.canViewUnit && hasPermission(EQUIPMENT_PERMISSIONS.vehicleView);
  // V3-057: Pictures and Documents apart; the generated record is shown with the actions.
  const attachments = t.files.filter((f) => !f.isRecord);
  const pictures = attachments.filter((f) => f.kind === 'picture' || f.photoSide);
  const documents = attachments.filter((f) => !pictures.includes(f));
  const legacy = (
    [
      ['General Condition & Status', t.conditionNotes],
      ['Other Items Sent', t.otherItemsSent],
      ['Unit Files & Warranty Documents', t.unitFilesNotes],
      ['Billing', t.billingBasis ? (t.billingBasis === 'OTHER' ? `Other: ${t.billingOther ?? ''}` : t.billingBasis.charAt(0) + t.billingBasis.slice(1).toLowerCase()) : null],
    ] as Array<[string, string | null]>
  ).filter(([, v]) => Boolean(v)) as Array<[string, string]>;
  const record = t.files.find((f) => f.isRecord);
  const progress = transferProgress(t);
  const rejectReason = dialog?.kind === 'reject' && !text.trim() ? 'Give a reason for rejecting.' : undefined;

  return (
    <div className="tw-w-full tw-p-4">
      <Link
        to="/equipment/transfers"
        className="tw-mb-3 tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-text-content-secondary tw-no-underline hover:tw-text-content"
      >
        <i className="bi bi-arrow-left" aria-hidden="true" />
        Back to Transfers
      </Link>

      <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-4 tw-shadow-card lg:tw-flex-row lg:tw-items-start lg:tw-justify-between">
        <div className="tw-min-w-0">
          <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Equipment Transfer #{t.id}</div>
          <h1 className="tw-mb-1 tw-text-xl tw-font-semibold">
            {t.unit.unitNumber}
            <span className="tw-ml-2 tw-text-base tw-font-normal tw-text-content-secondary">{t.unit.description}</span>
          </h1>
          <div className="tw-mb-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm">
            <span className="tw-font-medium">
              {t.from.label} <i className="bi bi-arrow-right" aria-label="to" /> {t.to.label}
            </span>
            <StatusBadge status={t.status} vocabulary="transfer" label={TRANSFER_STATUS_LABEL[t.status]} />
            {!t.permanent && <StatusBadge status="temporary" variant={t.overdueToReturn ? 'danger' : 'info'} label={t.overdueToReturn ? 'Overdue to return' : 'Temporary'} />}
          </div>
          {progress && <p className="tw-mb-0 tw-text-sm tw-text-content-secondary">{progress}</p>}
          {t.status === 'REJECTED' && t.rejectionReason && (
            <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-danger">Rejected: {t.rejectionReason}</p>
          )}
        </div>
        <div className="tw-flex tw-flex-wrap tw-gap-2 lg:tw-justify-end">
          {t.can.decide && (
            <>
              <Button size="sm" onClick={() => open({ kind: 'approve' })}>
                Approve
              </Button>
              <Button size="sm" variant="outline-danger" onClick={() => open({ kind: 'reject' })}>
                Reject
              </Button>
            </>
          )}
          {t.can.complete && (
            <Button size="sm" onClick={() => open({ kind: 'complete' })}>
              Mark Transfer Complete
            </Button>
          )}
          {/* A return starts from the unit's new area, so only someone who can open the unit there. */}
          {t.can.startReturn && canOpenUnit && (
            <Button size="sm" variant="outline-primary" onClick={() => open({ kind: 'return' })}>
              Start Return Transfer
            </Button>
          )}
          {record && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => window.open(vehicleTransferService.fileUrl(t.id, record.id), '_blank', 'noopener')}
            >
              <i className="bi bi-file-earmark-pdf tw-mr-1" aria-hidden="true" />
              Transfer Record
            </Button>
          )}
          {t.can.retryPdf && (
            <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => void act(() => vehicleTransferService.retryPdf(t.id))}>
              Attach the Record PDF
            </Button>
          )}
          {canOpenUnit && (
            <Button size="sm" variant="outline-secondary" onClick={() => navigate(`/equipment/${t.vehicleId}`)}>
              Open Unit
            </Button>
          )}
          {t.can.cancel && (
            <Button size="sm" variant="outline-danger" onClick={() => open({ kind: 'cancel' })}>
              Cancel Transfer
            </Button>
          )}
        </div>
      </div>

      {actionError && !dialog && <Alert variant="danger">{actionError}</Alert>}

      <div className="tw-grid tw-gap-4 xl:tw-grid-cols-[2fr_1fr]">
        <div>
          <Section title="Equipment Information">
            <dl className="tw-mb-0 tw-grid tw-grid-cols-2 tw-gap-4 md:tw-grid-cols-4">
              <Field label="Unit #">{t.unit.unitNumber}</Field>
              <Field label={t.meterUnit === 'HR' ? 'Hours' : 'Mileage'}>
                {t.meterOut != null ? (
                  <span className="tw-tabular-nums">{`${t.meterOut.toLocaleString('en-CA')} ${t.meterUnit === 'HR' ? 'hours' : 'km'}`}</span>
                ) : null}
              </Field>
              <Field label="Rate">
                {t.rate != null
                  ? `${t.rate.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', currencyDisplay: 'narrowSymbol' })} / h`
                  : null}
              </Field>
              <Field label="Year">{t.unit.year}</Field>
              <Field label="Make">{t.unit.make}</Field>
              <Field label="Model">{t.unit.model}</Field>
              <div className="tw-col-span-2">
                <Field label="VIN">{t.unit.vin}</Field>
              </div>
              <div className="tw-col-span-2 md:tw-col-span-4">
                <Field label="Description">{t.unit.description}</Field>
              </div>
              <Field label="Date of Move">{formatDay(t.effectiveDate)}</Field>
              <Field label="To Foreman Area">{t.toForemanArea?.name}</Field>
              <Field label="To Yard">{t.toYard?.name}</Field>
            </dl>
          </Section>

          <Section title="Pictures">
            <TransferFileList transferId={t.id} files={pictures} empty="No pictures." />
          </Section>

          <Section title="Accessories">
            {t.accessories ? <p className="tw-mb-0 tw-whitespace-pre-wrap tw-text-sm">{t.accessories}</p> : <EmptyValue />}
          </Section>

          <Section title="Documents">
            <TransferFileList transferId={t.id} files={documents} empty="No documents." />
          </Section>

          <Section title="Comments">
            {t.comments ? <p className="tw-mb-0 tw-whitespace-pre-wrap tw-text-sm">{t.comments}</p> : <EmptyValue />}
            {legacy.length > 0 && (
              // Transfers sent on the earlier form keep what was entered there.
              <dl className="tw-mb-0 tw-mt-3 tw-grid tw-gap-3 tw-border-t tw-border-line tw-pt-3 md:tw-grid-cols-2">
                {legacy.map(([label, value]) => (
                  <Field key={label} label={label}>
                    <span className="tw-whitespace-pre-wrap">{value}</span>
                  </Field>
                ))}
              </dl>
            )}
          </Section>
        </div>

        <div>
          <Section title="Approvals">
            <ul className="tw-mb-0 tw-flex tw-flex-col tw-gap-3 tw-pl-0">
              {t.approvals.map((a) => (
                <li key={a.id} className="tw-list-none tw-rounded tw-border tw-border-line tw-px-3 tw-py-2">
                  <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                    <div>
                      <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">
                        {APPROVAL_KIND_LABEL[a.kind]}
                        {a.kind === 'SENDING' ? ` · ${t.from.label}` : a.kind === 'RECEIVING' ? ` · ${t.to.label}` : ''}
                      </div>
                      <div className="tw-text-sm tw-font-medium">{a.approver.name}</div>
                    </div>
                    <StatusBadge
                      status={a.decision}
                      variant={a.decision === 'PENDING' ? 'orange' : a.decision === 'APPROVED' ? 'success' : 'danger'}
                      label={a.decision === 'PENDING' ? 'Waiting' : a.decision === 'APPROVED' ? 'Approved' : 'Rejected'}
                    />
                  </div>
                  {decisionText(a) && <div className="tw-mt-1 tw-text-xs tw-text-content-secondary">{decisionText(a)}</div>}
                  {a.comment && <div className="tw-mt-1 tw-text-sm tw-italic">“{a.comment}”</div>}
                  {t.can.reassign && a.decision === 'PENDING' && (
                    <Button size="sm" variant="link" className="tw-px-0" onClick={() => open({ kind: 'reassign', slot: a.kind })}>
                      Reassign
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            <p className="tw-mb-0 tw-mt-3 tw-text-xs tw-text-content-muted">
              Requested by {t.requestedBy.name} on {formatWhen(t.requestedAt)}.
              {t.completedBy && ` Completed by ${t.completedBy.name}${t.sageReference ? ` (Sage ${t.sageReference})` : ''}.`}
            </p>
          </Section>

          <Section title="History">
            <ol className="tw-mb-0 tw-flex tw-flex-col tw-gap-2 tw-pl-0">
              {t.events.map((e) => {
                // V3-069: plain language, no setting or event names.
                const { title, detail } = transferEventText(e);
                return (
                  <li key={e.id} className="tw-list-none tw-text-sm">
                    <div className="tw-flex tw-justify-between tw-gap-2">
                      <span className="tw-font-medium">{title}</span>
                      <span className="tw-whitespace-nowrap tw-text-xs tw-text-content-muted">{formatWhen(e.occurredAt)}</span>
                    </div>
                    {detail && <div className="tw-text-xs tw-text-content-secondary">{detail}</div>}
                  </li>
                );
              })}
            </ol>
          </Section>
        </div>
      </div>

      <Modal show={dialog?.kind === 'approve' || dialog?.kind === 'reject'} onHide={() => setDialog(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{dialog?.kind === 'reject' ? 'Reject Transfer' : 'Approve Transfer'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-text-sm">
            Unit {t.unit.unitNumber}, {t.from.label} to {t.to.label}, as the {t.can.decide ? APPROVAL_KIND_LABEL[t.can.decide].toLowerCase() : ''}.
          </p>
          {dialog?.kind === 'reject' && (
            <p className="tw-text-sm tw-text-content-secondary">Rejecting closes the transfer. The requester is emailed your reason.</p>
          )}
          <Form.Group controlId="transfer-decision-comment">
            <Form.Label>
              {dialog?.kind === 'reject' ? (
                <>
                  Reason <span className="tw-text-danger">*</span>
                </>
              ) : (
                'Comment'
              )}
            </Form.Label>
            <Form.Control as="textarea" rows={3} value={text} onChange={(e) => setText(e.target.value)} />
          </Form.Group>
          {actionError && <Alert variant="danger" className="tw-mb-0 tw-mt-3">{actionError}</Alert>}
        </Modal.Body>
        <Modal.Footer disabledReason={rejectReason}>
          <Button variant="secondary" onClick={() => setDialog(null)} disabled={busy}>
            Back
          </Button>
          <Button
            variant={dialog?.kind === 'reject' ? 'danger' : 'primary'}
            disabled={busy || !!rejectReason}
            disabledReason={rejectReason}
            onClick={() => void act(() => vehicleTransferService.decide(t.id, dialog?.kind === 'reject' ? 'REJECTED' : 'APPROVED', text))}
          >
            {dialog?.kind === 'reject' ? 'Reject' : 'Approve'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={dialog?.kind === 'complete'} onHide={() => setDialog(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Mark Transfer Complete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-text-sm">
            Confirm the transfer of unit {t.unit.unitNumber} to {t.to.label} has been applied in Sage. The unit moves to {t.to.label} in WRAP now,
            and the signed record is attached to it.
          </p>
          <div className="tw-grid tw-gap-3 md:tw-grid-cols-2">
            <Form.Group controlId="transfer-sage-ref">
              <Form.Label>Sage Reference</Form.Label>
              <Form.Control value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. journal entry #" />
            </Form.Group>
            <Form.Group controlId="transfer-sage-date">
              <Form.Label>Applied in Sage On</Form.Label>
              <Form.Control type="date" value={sageDate} onChange={(e) => setSageDate(e.target.value)} />
            </Form.Group>
          </div>
          {actionError && <Alert variant="danger" className="tw-mb-0 tw-mt-3">{actionError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDialog(null)} disabled={busy}>
            Back
          </Button>
          <Button disabled={busy} onClick={() => void act(() => vehicleTransferService.complete(t.id, text, sageDate))}>
            {busy ? 'Completing…' : 'Mark complete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={dialog?.kind === 'cancel'} onHide={() => setDialog(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Transfer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-text-sm">The request is withdrawn and the unit stays in {t.from.label}.</p>
          <Form.Group controlId="transfer-cancel-reason">
            <Form.Label>Reason</Form.Label>
            <Form.Control value={text} onChange={(e) => setText(e.target.value)} />
          </Form.Group>
          {actionError && <Alert variant="danger" className="tw-mb-0 tw-mt-3">{actionError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDialog(null)} disabled={busy}>
            Back
          </Button>
          <Button variant="danger" disabled={busy} onClick={() => void act(() => vehicleTransferService.cancel(t.id, text))}>
            Cancel Transfer
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={dialog?.kind === 'reassign'} onHide={() => setDialog(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Reassign Approval</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-text-sm">
            Who should approve as {dialog?.kind === 'reassign' ? APPROVAL_KIND_LABEL[dialog.slot].toLowerCase() : ''}? They are emailed the request.
          </p>
          <TransferPersonPicker id="transfer-reassign" value={person} onChange={setPerson} />
          {actionError && <Alert variant="danger" className="tw-mb-0 tw-mt-3">{actionError}</Alert>}
        </Modal.Body>
        <Modal.Footer disabledReason={!person ? 'Choose a person.' : undefined}>
          <Button variant="secondary" onClick={() => setDialog(null)} disabled={busy}>
            Back
          </Button>
          <Button
            disabled={busy || !person}
            disabledReason={!person ? 'Choose a person.' : undefined}
            onClick={() => dialog?.kind === 'reassign' && person && void act(() => vehicleTransferService.reassign(t.id, dialog.slot, person.id))}
          >
            Reassign
          </Button>
        </Modal.Footer>
      </Modal>

      {dialog?.kind === 'return' && (
        <RequestTransferModal
          vehicleId={t.vehicleId}
          show
          returnOf={t}
          onHide={() => setDialog(null)}
          onCreated={(created) => {
            setDialog(null);
            notifyApprovalsChanged();
            navigate(`/equipment/transfers/${created.id}`);
          }}
        />
      )}
    </div>
  );
};

export default TransferDetailPage;
